import { motion, AnimatePresence } from "framer-motion";
import { fadeUp } from "../../animations/variants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useState } from "react";

const StepBooking = ({
  booking,
  setBooking,
  selectedDate,
  setSelectedDate,
  onNext,
  activeStep,
  setActiveStep,
  steps,
}) => {
  const SERVICE_OPTIONS = [
    "Drinks in the Greenhouse",
    "Drinks on the Balcony",
    "Dinner",
    "Dinner on the Balcony",
  ];

  const generateTimeSlots = (
    baseDate,
    startHour,
    startMinute,
    endHour,
    endMinute,
    stepMinutes = 30,
  ) => {
    const slots = [];

    const start = new Date(baseDate);
    start.setHours(startHour, startMinute, 0, 0);

    const end = new Date(baseDate);
    end.setHours(endHour, endMinute, 0, 0);

    while (start < end) {
      slots.push(
        start
          .toLocaleTimeString("en-NZ", {
            hour: "numeric",
            minute: "2-digit",
          })
          .toLowerCase(),
      );
      start.setMinutes(start.getMinutes() + stepMinutes);
    }

    return slots;
  };

  const getTimeSlotsForDate = (date) => {
    if (!date) return [];

    const day = date.getDay(); // 0 = Sun, 5 = Fri, 6 = Sat
    const isWeekend = day === 0 || day === 6; // Sun or Sat

    const lunch = generateTimeSlots(date, 11, 30, 15, 0);
    const dinner = generateTimeSlots(
      date,
      17,
      0,
      isWeekend ? 22 : 21,
      isWeekend ? 0 : 30,
    );

    return [...lunch, ...dinner];
  };

  const timeSlots = getTimeSlotsForDate(selectedDate);

  /** ✅ FINAL STATE */
  const isComplete = Boolean(
    selectedDate && booking.persons > 0 && booking.area && booking.time,
  );

  const [personModal, setPersonModal] = useState(false);
  const [serviceModal, setServiceModal] = useState(false);
  const [timeModal, setTimeModal] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {/* LEFT – calendar placeholder */}
      <motion.div
        variants={fadeUp}
        className="flex flex-col items-center justify-center"
      >
        <DatePicker
          selected={selectedDate}
          onChange={(date) => {
            setActiveStep(steps.SERVICE);
            setSelectedDate(date);

            setBooking((b) => ({
              ...b,
              time: "",
            }));
          }}
          inline
          minDate={new Date()} // disable past dates
          calendarClassName="booking-datepicker mt-2"
        />
      </motion.div>

      {/* RIGHT */}
      <div className="space-y-0">
        (
        <>
          <AnimatePresence>
            {activeStep === steps.SERVICE && (
              <>
                <div className="relative">
                  <div
                    onClick={() => {
                      setPersonModal(!personModal);
                    }}
                    className="border rounded bg-darkbg shadow-2xl p-4 cursor-pointer hover:border-neonYellow text-text mt-0"
                  >
                    {booking.persons > 0
                      ? `Booking for ${booking.persons} people`
                      : "Select number of people"}
                  </div>{" "}
                </div>
                {personModal && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="overflow-hidden border border-t-0 rounded-b-lg bg-white p-6 text-text"
                  >
                    <p className="font-medium mb-4">Booking for</p>

                    <div className="flex justify-center gap-6">
                      <button
                        onClick={() =>
                          setBooking((b) => ({
                            ...b,
                            persons: Math.max(1, b.persons - 1),
                          }))
                        }
                      >
                        –
                      </button>

                      <span className="text-2xl">{booking.persons}</span>

                      <button
                        onClick={() =>
                          setBooking((b) => ({
                            ...b,
                            persons: b.persons + 1,
                          }))
                        }
                      >
                        +
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        setPersonModal(!personModal);
                      }}
                      className="mt-6 w-full bg-darkbg text-neonYellow py-2 rounded hover:bg-neonYellow hover:text-navy"
                      disabled={booking.persons < 1}
                    >
                      Confirm
                    </button>
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {activeStep === steps.SERVICE && (
              <>
                <div className="relative">
                  <div
                    onClick={() => {
                      setServiceModal(!personModal);
                    }}
                    className="border rounded bg-darkbg shadow-2xl p-4 cursor-pointer hover:border-neonYellow text-text mt-2"
                  >
                    {booking.area || "Select service"}
                  </div>{" "}
                </div>
                {serviceModal && (
                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    exit="hidden"
                    className="overflow-hidden border border-border rounded bg-darkbg p-6 text-text"
                    transition={{
                      height: { duration: 0.5, ease: "easeInOut" },
                      opacity: { duration: 0.2 },
                    }}
                    onClick={() => setServiceModal(!serviceModal)}
                  >
                    <p className="font-medium mb-4">
                      Which service are you booking for?
                    </p>

                    <div className="grid gap-3">
                      {SERVICE_OPTIONS.map((option) => {
                        const isSelected = booking.area === option;

                        return (
                          <button
                            key={option}
                            onClick={() =>
                              setBooking((b) => ({ ...b, area: option }))
                            }
                            className={`flex items-center gap-3 px-4 py-3 rounded-md border transition
                ${
                  isSelected
                    ? "border-neonYellow bg-neonYellow/10 text-neonYellow"
                    : "border-gray-300 hover:border-neonYellow/50"
                }`}
                          >
                            <span
                              className={`w-4 h-4 rounded-full border flex items-center justify-center
                  ${isSelected ? "border-neonYellow" : "border-gray-400"}`}
                            >
                              {isSelected && (
                                <span className="w-2 h-2 bg-neonYellow rounded-full" />
                              )}
                            </span>

                            <span className="text-sm">{option}</span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {activeStep === steps.SERVICE && (
              <>
                <div className="relative">
                  <div
                    onClick={() => {
                      setTimeModal(!timeModal);
                    }}
                    className="border rounded bg-darkbg shadow-2xl p-4 cursor-pointer hover:border-neonYellow text-text mt-2"
                  >
                    {booking.time || "Select Time"}
                  </div>{" "}
                </div>
                {timeModal && (
                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    exit="hidden"
                    className="border rounded-lg p-6 text-text"
                    transition={{
                      height: { duration: 0.5, ease: "easeInOut" },
                      opacity: { duration: 0.2 },
                    }}
                  >
                    <p className="font-medium mb-4">Select a time</p>

                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {timeSlots.map((slot) => {
                        const isSelected = booking.time === slot;

                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => {
                              setTimeModal(!timeModal);
                              setBooking((b) => ({
                                ...b,
                                time: slot,
                              }));
                            }}
                            className={`
                px-3 py-2 rounded-full border text-sm transition
                ${
                  isSelected
                    ? "bg-neonYellow text-black border-neonYellow"
                    : "border-gray-300 hover:border-neonYellow/60"
                }
              `}
                          >
                            {slot}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>
        </>
        )
        <div className="flex justify-end">
          <button
            onClick={onNext}
            disabled={!isComplete}
            className={`flex justify-end px-8 py-3 rounded transition mt-4 block lg:hidden
    ${
      booking.time
        ? "bg-darkbg text-neonYellow"
        : "bg-gray-300 text-gray-500 cursor-not-allowed"
    }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepBooking;
